import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  AUTO_MODE_MODELS,
  categoryIcons,
  getAutoModeFromModelName,
  getAutoModeModelName,
  isAutoFamilyModelName,
  models,
  thinkingModels,
  AUTO_MODEL,
  normalize,
  detectProvider,
} from "../AiBotData";
import TiptapEditor, {
  TiptapEditorMethods,
} from "@/components/shared/MarkdownEditor/TiptapEditor";
import {
  AIAgentIcon,
  SupportIcon,
  SalesIcon,
  LanguageIcon,
  CodingExpertIcon,
  ResetIconAISettings,
  CustomIcon,
  PromptIcon,
  LogicJumpIcon,
  UpwardsArrowSvg,
  BrainSvg,
} from "@/assets/AiBotAssets";
import {
  axiosPost,
  axiosGet,
  axiosDelete,
  axiosPatch,
  axiosPut,
} from "@/services/axiosService";
import { userSlice } from "@/redux/User";
import { useDispatch, useSelector } from "react-redux";
import ThreeDot from "@/components/loaders/ThreeDot";
import { StarsIcon2 } from "./AiBotAssets";
import { AnimatePresence, motion } from "framer-motion";
import { LockIcon } from "@/assets/AiBotAssets";
import useGetBotAttributes from "@/hooks/useGetAttributes";

import { Link, useNavigate } from "react-router-dom";
import { BotContext } from "../store";
import ByokOnboarding from "@/pages/onboarding/byokOnboarding";
import UnifiedModelSelector from "./UnifiedModelSelector";
import ModalWrapper from "./ModalWrapper";
import { useUserDetails } from "@/react-query/useUserDetails";
import { useQueryClient } from "@tanstack/react-query";
import DeleteAgentList from "./DeleteAgentList";
import BooleanModal from "@/components/builder/BooleanModal";
import { aiConfigElements, AIConfiguration } from "./AIConfiguration";
import { State } from "@/redux/Types";
import { sendEventToOutgrow } from "@/pages/cbb-builder/outgrow-event.service";
import useGetSingleCompany from "../react-query/useGetSingleCompany";

interface CustomAIConfig {
  _id: string;
  name?: string;
  model?: string;
  icon?: string;
  [key: string]: any;
}

interface EnhancedModel {
  name: string;
  displayName: string;
  url: string;
  autoMode?: "lowCost" | "balanced" | "quality";
  isCustom?: boolean;
  configId?: string;
  config?: CustomAIConfig;
  categories?: string[];
}

const CUSTOM_PROMPT_LIMIT_EXEMPT_EMAILS = new Set<string>([
  "akshay+2@venturepact.com",
  "tarun@mastersunion.org",
  "opennirvana49@gmail.com",
]);
const CUSTOM_PROMPT_MAX_LENGTH = 30000;

const extractObjectId = (value: any): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (typeof value._id === "string") return value._id;
    if (typeof value.$oid === "string") return value.$oid;
  }
  return null;
};

const getPersistedModelName = (botState: any): string | undefined => {
  return typeof botState?.botModel === "string" &&
    botState.botModel.trim() !== ""
    ? botState.botModel
    : undefined;
};

const hasExplicitlyClearedModelSelection = (botState: any): boolean => {
  return botState?.botModel === null;
};

const getClearedModelSettings = (botState: any) =>
  botState?.aiConfig?.modelSettings?.map((setting: any) => ({
    ...setting,
    selected: false,
  })) ?? [];

const getClearedAiConfig = (botState: any) => ({
  ...(botState?.aiConfig || {}),
  model: null,
  botModel: null,
  modelSettings: getClearedModelSettings(botState),
});

const getClearedAiBotConfig = (botState: any) => ({
  ...(botState?.aiBotConfig || {}),
  configId: null,
  model: null,
});

const resetPreviewModelState = (embedConfig?: any) => {
  window.ChatbotBuilder?.setUserData({
    aiModel: "null",
  });

  if (typeof (window as any).reloadPreviewIframe === "function") {
    (window as any).reloadPreviewIframe(embedConfig ?? {});
  }
};

const DEFAULT_CUSTOM_VARIABLE = [
  "query",
  "sources",
  "fallback",
  "language",
  "wordLimit",
  "tone",
];

const providerIcons: Record<string, string> = {
  openai:
    "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
  anthropic:
    "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
  google:
    "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
  groq: "https://dlvkyia8i4zmz.cloudfront.net/vU4PFf4RRgKVLEWz3PZf_groq_logo.svg",
  deepseek:
    "https://dlvkyia8i4zmz.cloudfront.net/14mmMzyORaS0FZ38yD9f_Frame__11_.svg",
  openrouter:
    "https://dlvkyia8i4zmz.cloudfront.net/b4nHxmER7jWwIQWXlzZg_svgviewer_output__6_.svg",
  grok: "https://dlvkyia8i4zmz.cloudfront.net/5p8NzQLS3KdleTBwHJBg_svgviewer_output__3_.svg",
};

const promptModification = [
  { name: "AI agent", image: <AIAgentIcon /> },
  { name: "Customer support agent", image: <SupportIcon /> },
  { name: "Sales agent", image: <SalesIcon /> },
  { name: "Language tutor", image: <LanguageIcon /> },
  { name: "Coding expert", image: <CodingExpertIcon /> },
  { name: "Logic jump", image: <LogicJumpIcon /> },
  { name: "Custom", image: <CustomIcon /> },
];

function AIBotTab_5({
  botData,
  onDataChange,
  setSelectedSetupTab,
  allowedModels,
  plan,
  defaultByokConfigId,
  onTryNow,
  hideApiKeyOption = false,
  hideByokOptions = false,
  hideAdvancedSettings = false,
  hideModelQualities = false,
  isReadOnly = false,
  confirmTryNowNavigation = false,
}) {
  const { data: selectedCompany, refetch: refetchCompany } =
    useGetSingleCompany();
  const dropdownRef = useRef<any>();
  const configDropdownRef = useRef<any>();
  const attributeDropdownRef = useRef<any>();
  const fullscreenAttributeDropdownRef = useRef<any>();
  const advancedSettingsRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<TiptapEditorMethods>(null);
  const fullscreenEditorRef = useRef<TiptapEditorMethods>(null);
  const dispatch = useDispatch();
  const fromOutgrow = useSelector((state: State) => state.user.fromOutgrow);
  const role = useSelector((state: State) => state.user.role);
  const canManageProviderKeys = role === "admin";
  const byok = selectedCompany?.byok ?? false;
  const isNewUser = useSelector((state: State) => state.user.isNewUser);
  const userEmail = useSelector((state: State) => state.user.userEmail);
  const customPromptMaxLength = CUSTOM_PROMPT_LIMIT_EXEMPT_EMAILS.has(
    (userEmail ?? "").trim().toLowerCase(),
  )
    ? undefined
    : CUSTOM_PROMPT_MAX_LENGTH;

  // Enhanced state for AI models
  const [enhancedModels, setEnhancedModels] = useState<EnhancedModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<EnhancedModel | null>(
    null,
  );
  const [allAIConfig, setAllAIConfig] = useState<CustomAIConfig[]>([]);
  const [configLoader, setConfigLoader] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [configIdToDelete, setConfigIdToDelete] = useState<string | undefined>(
    undefined,
  );
  const [leaveModal, setLeaveModal] = useState(false);
  const [openAdvanceSettings, setOpenAdvanceSettings] = useState(false);
  const [byokOnboardingMode, setByokOnboardingMode] = useState<boolean>(false);
  const [onboardingFlowMode, setOnboardingFlowMode] = useState<
    "onboarding" | "add" | "edit"
  >("onboarding");
  const [editingConfigId, setEditingConfigId] = useState<string | undefined>(
    undefined,
  );
  const [editingConfigProvider, setEditingConfigProvider] = useState<
    string | undefined
  >(undefined);
  const [sourceConfigForNewModel, setSourceConfigForNewModel] = useState<
    CustomAIConfig | undefined
  >(undefined);

  const queryClient = useQueryClient();

  const [store, storeDispatch] = useContext(BotContext);
  const { data: userDetails } = useUserDetails();
  const navigate = useNavigate();
  const configs: any = store.config || [];
  const currentConfig: any = store.curentCustomModelData || {};

  const shouldHideByokOptions =
    hideByokOptions || (hideApiKeyOption && userDetails?.appSumo === true);
  const isCustomModel =
    !shouldHideByokOptions && !!botData?.aiBotConfig?.configId;
  const handleTryNow = () => {
    if (confirmTryNowNavigation) {
      setLeaveModal(true);
      return;
    }

    onTryNow?.();
  };

  // If company-wide BYOK is active, we MUST use custom models.
  // If it's disabled, we MUST use default models (unless it's an override case like AppSumo,
  // but the user's latest request implies byok is the master toggle).

  const [editorHeight, setEditorHeight] = useState(250);
  const [isPromptFullscreen, setIsPromptFullscreen] = useState(false);

  const [prompts, setPrompts] = useState(() => {
    const initialPrompts = [...(botData?.prompts ?? [])];

    // Add custom prompt if exists and not already there
    if (
      botData?.aiBotConfig?.customPromptCustom &&
      !initialPrompts.some(
        (p) => p.prompt === botData.aiBotConfig.customPromptCustom,
      )
    ) {
      initialPrompts.push({
        name: "Custom",
        prompt: botData.aiBotConfig.customPromptCustom,
      });
    }

    return initialPrompts;
  });
  useEffect(() => {
    if (!botData?.prompts?.length) return;
    setPrompts((prev) => {
      if (prev.length > 0) return prev;
      const synced = [...botData.prompts];
      if (
        botData?.aiBotConfig?.customPromptCustom &&
        !synced.some((p) => p.prompt === botData.aiBotConfig.customPromptCustom)
      ) {
        synced.push({
          name: "Custom",
          prompt: botData.aiBotConfig.customPromptCustom,
        });
      }
      return synced;
    });
  }, [botData?.prompts]);

  const [customPromptErr, setCustomPromptErr] = useState("");
  const [promptIndex, setPromptIndex] = useState(0);
  const hasInitializedPromptTab = useRef(false);
  const [loader, setLoader] = useState(false);
  const [dropdowns, setDropdowns] = useState({
    model: false,
    attributes: false,
    fullscreenAttributes: false,
    configuration: false,
  });
  const { data: attributeData } = useGetBotAttributes(botData.botCustomId);
  const updateState = (data: any) => {
    setSourceConfigForNewModel(undefined);
    setEditingConfigId(data._id);
    setEditingConfigProvider(data.provider);
    setOnboardingFlowMode("edit");
    setByokOnboardingMode(true);
  };
  const handleDelete = async (id: string | undefined) => {
    setLoader(true);

    try {
      await axiosDelete(`/configurations/deleteAIConfig?configId=${id}`);

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "success",
          message: "Configuration Deleted Successfully!",
        }),
      );

      // Proactively clear the current bot's mapping if it matches the deleted configuration
      // if (
      //   byok &&
      //   extractObjectId(id) === extractObjectId(botData?.aiBotConfig?.configId)
      // ) {
      //   onDataChange("root", {
      //     ...botData,
      //     botModel: null,
      //     aiConfig: getClearedAiConfig(botData),
      //     aiBotConfig: getClearedAiBotConfig(botData),
      //   });
      //   resetPreviewModelState(botData?.embedConfig);
      // }

      try {
        await getAllAIConfig();
      } catch (refreshErr) {
        console.error("Deleted configuration, but refresh failed:", refreshErr);
      }

      setShowDeleteModal(false);
      queryClient.invalidateQueries({ queryKey: ["bot", "completeBot"] });
    } catch (err: any) {
      console.log(err);
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: "Failed to delete configuration.",
        }),
      );
      setShowDeleteModal(false);
    } finally {
      setLoader(false);
    }
  };

  const handleDirectModelUpdate = async (config: any) => {
    syncByokState(true);
    try {
      setLoader(true);

      const modelName = config.model;
      let found = false;
      let newModelSettings =
        botData?.aiConfig?.modelSettings?.map((setting: any) => {
          if (setting.modelName === modelName) {
            found = true;
            return { ...setting, selected: true };
          }
          return { ...setting, selected: false };
        }) ?? [];

      if (!found) {
        const isThinking = thinkingModels.includes(modelName);
        const newSetting = {
          modelName: modelName,
          temperature: 0,
          thinkingConfig: isThinking
            ? {
                enable: true,
                mode: "disabled",
                level: "minimal",
              }
            : null,
          selected: true,
        };
        newModelSettings.push(newSetting);
      }

      const isAuto = isAutoFamilyModelName(modelName);
      const autoMode =
        modelName === "auto"
          ? botData.aiBotConfig?.selectedMode ||
            botData.aiBotConfig?.maestroFlowConfig?.selectedMode ||
            "balanced"
          : getAutoModeFromModelName(modelName);
      const persistedModelName = isAuto ? "auto" : modelName;

      onDataChange("root", {
        ...botData,
        botModel: persistedModelName,
        aiConfig: {
          ...botData.aiConfig,
          modelSettings: newModelSettings,
        },
        aiBotConfig: {
          ...botData.aiBotConfig,
          configId: config._id,
          model: persistedModelName,
          ...(isAuto && {
            maestroFlowConfig: {
              ...(botData.aiBotConfig?.maestroFlowConfig || {}),
              selectedMode: autoMode,
            },
            selectedMode: autoMode,
          }),
        },
      });

      // Synchronize allAIConfig state so the ModelSelector gets the updated model for this config
      setAllAIConfig((prev) =>
        prev.map((c) =>
          c._id === config._id ? { ...c, model: modelName } : c,
        ),
      );

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "success",
          message: "Model Updated Successfully!",
        }),
      );

      // No need to call updateAIConfig API as we are saving it at bot level
      // No need to call getAllAIConfig as we updated botData locally
    } catch (err: any) {
      console.error(err);
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message:
            err.response?.data?.message ??
            err.message ??
            "Failed to update model.",
        }),
      );
    } finally {
      setLoader(false);
    }
  };

  const handleUpdateConfig = async (configId: string, updates: any) => {
    try {
      setLoader(true);
      const response = await axiosPatch(
        `/configurations/updateAIConfig?configId=${configId}`,
        updates,
      );
      if (response.status === 200) {
        dispatch(
          userSlice.actions.setToastPopUp({
            show: true,
            toastType: "success",
            message: "Configuration Updated Successfully!",
          }),
        );
        getAllAIConfig();
      }
    } catch (err: any) {
      console.error(err);
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message:
            err.response?.data?.message ??
            err.message ??
            "Failed to update configuration.",
        }),
      );
    } finally {
      setLoader(false);
    }
  };

  // Function to get all AI configurations
  const getAllAIConfig = async () => {
    setConfigLoader(true);
    try {
      const response = await axiosGet(`/configurations/allAIConfigs`);
      setAllAIConfig(response.data.data);
      const configs = response.data.data;

      // If the current agent's config is no longer in the list, clear it.
      const currentConfigId = extractObjectId(botData?.aiBotConfig?.configId);
      if (
        byok &&
        currentConfigId &&
        !configs.some((c: any) => extractObjectId(c?._id) === currentConfigId)
      ) {
        onDataChange("root", {
          ...botData,
          botModel: null,
          aiConfig: getClearedAiConfig(botData),
          aiBotConfig: getClearedAiBotConfig(botData),
        });
        resetPreviewModelState(botData?.embedConfig);
      } else if (byok && configs.length === 0 && currentConfigId) {
        onDataChange("root", {
          ...botData,
          botModel: null,
          aiConfig: getClearedAiConfig(botData),
          aiBotConfig: getClearedAiBotConfig(botData),
        });
        resetPreviewModelState(botData?.embedConfig);
      }

      // Logic for last key deletion
      if (configs.length === 0 && !isNewUser && userDetails?.appSumo === true) {
        if (!isNewUser) {
          onDataChange("root", {
            ...botData,
            botModel: null,
            aiConfig: getClearedAiConfig(botData),
            aiBotConfig: getClearedAiBotConfig(botData),
          });
          setSelectedModel(null);
          setSelectedModelSetting(null);
          resetPreviewModelState(botData?.embedConfig);
        }
        // This was the last key — disable BYOK and open adding key popup
        await axiosPut("/users/updateByok", {
          byok: false,
          defaultByokConfigId: null,
        });
        dispatch(userSlice.actions.setByok(false));
        setOnboardingFlowMode("add");
        setByokOnboardingMode(false);
      }

      storeDispatch({
        type: "set-config",
        config: response.data.data,
      });
      queryClient.setQueryData(
        ["configurations", "openai"],
        response.data.data ?? [],
      );
      setConfigLoader(false);
    } catch (err) {
      console.error("Error loading AI configs:", err);
      setConfigLoader(false);
    }
  };

  useEffect(() => {
    const rawConfigId = botData?.aiBotConfig?.configId;
    const configId =
      (typeof rawConfigId === "object" ? rawConfigId?._id : rawConfigId) ||
      null;
    let modelsArray: EnhancedModel[] = [AUTO_MODEL, ...models];
    const selectedSetting = botData?.aiConfig?.modelSettings?.find(
      (s) => s.selected,
    );
    setSelectedModelSetting(selectedSetting || null);

    if (userDetails?.appSumo === true && !isNewUser && configs.length === 0) {
      setSelectedModel(null);
      setSelectedModelSetting(null);
      resetPreviewModelState(botData?.embedConfig);
      return;
    }

    if (configId) {
      const customConfig = configs.find((c) => c._id === configId);
      if (customConfig) {
        const customModelName = botData?.botModel || customConfig.model;

        let matchedModel = customModelName
          ? modelsArray.find((m) => {
              const normM = normalize(m.name);
              const normC = normalize(customModelName);
              return (
                normM === normC ||
                (customModelName.includes("/") &&
                  m.name.endsWith(customModelName.split("/").pop()!)) ||
                (m.name.includes("/") &&
                  customModelName.endsWith(m.name.split("/").pop()!))
              );
            })
          : undefined;

        // If BYOK is active, validate that the matched model (if any) belongs to the config's provider.
        if (matchedModel && byok && customConfig.provider) {
          const p = customConfig.provider.toLowerCase();
          const n = (
            matchedModel.displayName ||
            matchedModel.name ||
            ""
          ).toLowerCase();
          let isMatch = false;
          if (p.includes("openrouter") || p.includes("open router"))
            isMatch = true; // OpenRouter handles all models
          else if (p.includes("google") || p.includes("gemini"))
            isMatch = n.includes("google") || n.includes("gemini");
          else if (p.includes("openai") || p.includes("gpt"))
            isMatch = n.includes("openai") || n.includes("gpt");
          else if (p.includes("anthropic") || p.includes("claude"))
            isMatch = n.includes("anthropic") || n.includes("claude");
          else if (p.includes("deepseek")) isMatch = n.includes("deepseek");
          else if (p.includes("groq"))
            isMatch =
              n.includes("groq") ||
              n.includes("kimi") ||
              n.includes("llama") ||
              n.includes("oss");
          else isMatch = n.includes(p);

          if (!isMatch) {
            matchedModel = undefined;
          }
        }

        if (!matchedModel && byok && customConfig.provider) {
          const p = customConfig.provider.toLowerCase();
          matchedModel = modelsArray.find((m) => {
            const n = (m.displayName || m.name || "").toLowerCase();
            if (p.includes("openrouter") || p.includes("open router"))
              return n.includes("openrouter");
            if (p.includes("google") || p.includes("gemini"))
              return n.includes("google") || n.includes("gemini");
            if (p.includes("openai") || p.includes("gpt"))
              return n.includes("openai") || n.includes("gpt");
            if (p.includes("anthropic") || p.includes("claude"))
              return n.includes("anthropic") || n.includes("claude");
            if (p.includes("deepseek")) return n.includes("deepseek");
            if (p.includes("groq"))
              return (
                n.includes("groq") ||
                n.includes("kimi") ||
                n.includes("llama") ||
                n.includes("oss")
              );
            return n.includes(p);
          });
        }

        setSelectedModel(
          matchedModel
            ? {
                ...matchedModel,
                isCustom: true,
                configId,
                config: customConfig,
              }
            : {
                name: customModelName || "",
                displayName: customConfig.name ?? customModelName,
                url: customConfig.icon ?? "",
                isCustom: true,
                configId,
                config: customConfig,
              },
        );

        // SYNC: If we auto-corrected or defaulted to a recommended model
        if (
          botData &&
          Object.keys(botData).length &&
          matchedModel &&
          botData?.botModel !== matchedModel.name
        ) {
          onDataChange("root", {
            ...botData,
            botModel: matchedModel.name,
            aiBotConfig: {
              ...botData.aiBotConfig,
              configId,
              oldConfigSelected: configId,
            },
          });
        }
      }
    } else {
      const savedDefaultName = getPersistedModelName(botData);
      const shouldPreserveEmptySelection =
        !savedDefaultName && hasExplicitlyClearedModelSelection(botData);
      // || botData?.aiBotConfig?.fallbackModel;
      let defaultSelected = modelsArray.find(
        (m) => m.name === savedDefaultName,
      );

      // ✅ FALLBACK: If BYOK is OFF and the currently selected platform model is restricted under the user's plan,
      // automatically fallback to the recommended model for that provider.
      if (!byok && defaultSelected) {
        const isAllowed =
          plan?.name === "Trial" ||
          plan?.name === "outgrow" ||
          plan?.name === "Lifetime" ||
          (defaultSelected.autoMode &&
            allowedModels?.some((am: any) => normalize(am) === "auto")) ||
          allowedModels?.some((am: any) => {
            const nm = normalize(defaultSelected!.name);
            const na = normalize(am);
            return (
              nm === na ||
              defaultSelected!.name.toLowerCase() === am.toLowerCase()
            );
          });

        if (!isAllowed) {
          const provider = detectProvider(defaultSelected);
          const providerModels = modelsArray.filter(
            (m) => detectProvider(m) === provider,
          );
          const recommendedUnlockedModel =
            providerModels.find((m) => {
              const providerModelAllowed =
                plan?.name === "Trial" ||
                plan?.name === "outgrow" ||
                plan?.name === "Lifetime" ||
                (m.name === "auto" &&
                  allowedModels?.some((am: any) => normalize(am) === "auto")) ||
                allowedModels?.some((am: any) => {
                  const nm = normalize(m.name);
                  const na = normalize(am);
                  return nm === na || m.name.toLowerCase() === am.toLowerCase();
                });

              return (
                m.displayName.toLowerCase().includes("(recommended)") &&
                providerModelAllowed
              );
            }) ||
            providerModels.find((m) => {
              return (
                plan?.name === "Trial" ||
                plan?.name === "outgrow" ||
                plan?.name === "Lifetime" ||
                (m.name === "auto" &&
                  allowedModels?.some((am: any) => normalize(am) === "auto")) ||
                allowedModels?.some((am: any) => {
                  const nm = normalize(m.name);
                  const na = normalize(am);
                  return nm === na || m.name.toLowerCase() === am.toLowerCase();
                })
              );
            });

          if (recommendedUnlockedModel) {
            defaultSelected = recommendedUnlockedModel;
          }
        }
      }

      const finalModel = defaultSelected
        ? defaultSelected
        : shouldPreserveEmptySelection
          ? null
          : modelsArray[0];

      if (
        byok &&
        !botData?.aiBotConfig?.configId &&
        (!defaultSelected || configs.length === 0)
      ) {
        // If BYOK is active but no custom model is selected, and no platform model matches, then null.
        // Also covers the case where all keys were deleted but stale server data carried a botModel value.
        setSelectedModel(null);
        setSelectedModelSetting(null);
      } else if (!finalModel) {
        setSelectedModel(null);
        setSelectedModelSetting(null);
      } else {
        setSelectedModel(finalModel);

        if (
          botData &&
          Object.keys(botData).length &&
          !byok &&
          botData?.botModel !== finalModel.name
        ) {
          onDataChange("root", {
            ...botData,
            botModel: finalModel.name,
            aiBotConfig: {
              ...botData.aiBotConfig,
              configId: null,
              model: null,
            },
          });
        }
      }
    }

    setEnhancedModels(modelsArray);
  }, [
    botData?.aiBotConfig?.configId,
    botData?.aiBotConfig?.maestroFlowConfig?.selectedMode,
    botData?.aiConfig?.model,
    botData?.botModel,
    models,
    allAIConfig,
    configs,
    byok,
    defaultByokConfigId,
  ]);

  const syncByokState = async (newState: boolean) => {
    if (byok === newState) return;
    try {
      await axiosPut("/users/updateByok", {
        byok: newState,
        defaultByokConfigId: newState ? defaultByokConfigId : null,
      });
      dispatch(userSlice.actions.setByok(newState));
      // ✅ Invalidate the company query to ensure the parent component (Builder.tsx)
      // refreshes its byok prop from the backend.
      queryClient.invalidateQueries({ queryKey: ["company"] });
    } catch (err) {
      console.error("Failed to sync BYOK state:", err);
    }
  };

  useEffect(() => {
    if (currentConfig?.saveConfig?._id) {
      onDataChange("root", {
        ...botData,
        botModel: currentConfig?.saveConfig?.model || null,
        aiBotConfig: {
          ...botData.aiBotConfig,
          configId: currentConfig?.saveConfig?._id || null,
        },
      });

      window.ChatbotBuilder?.setUserData({
        aiModel: currentConfig?.saveConfig?.model || "null",
      });

      handleConfigSelect(currentConfig?.saveConfig?._id);
      setSelectedModel({
        name: currentConfig.saveConfig.model,
        displayName: currentConfig.saveConfig.name,
        url: currentConfig.saveConfig.icon,
        isCustom: true,
        configId: currentConfig.saveConfig._id,
        config: currentConfig.saveConfig,
      });

      storeDispatch({
        type: "set-currentCustomModelData",
        curentCustomModelData: {},
      });
    }
  }, [currentConfig.saveConfig, currentConfig]);

  useEffect(() => {
    getAllAIConfig();
  }, [botData?.aiBotConfig?.configId]);

  useEffect(() => {
    const selected = botData?.aiConfig?.modelSettings?.find(
      (s: any) => s.selected,
    );
    if (selected) setSelectedModelSetting(selected);
  }, [botData?.aiConfig?.modelSettings]);

  // State to hold the selected modelSetting object
  type ThinkingConfig = {
    enable: boolean;
    level: string;
    mode: string;
  };

  const [selectedModelSetting, setSelectedModelSetting] = useState<{
    modelName: string;
    temperature: number;
    thinkingConfig: ThinkingConfig | null;
    selected: boolean;
  } | null>(null);

  const handleModelSelect = (model: EnhancedModel) => {
    syncByokState(false);
    setSelectedModel(model);

    const isNoneOfThese = model.name === "none of these";
    const selectedAutoMode = getAutoModeFromModelName(model.name);
    const modelNameForSetting = isNoneOfThese
      ? model.name
      : isAutoFamilyModelName(model.name)
        ? "auto"
        : model.name;

    window.ChatbotBuilder?.setUserData({
      aiModel: isNoneOfThese ? "null" : modelNameForSetting,
    });

    let found = false;
    let newModelSettings =
      botData?.aiConfig?.modelSettings?.map((setting) => {
        if (setting.modelName === modelNameForSetting) {
          found = true;
          setSelectedModelSetting(setting);
          return { ...setting, selected: true };
        }
        return { ...setting, selected: false };
      }) ?? [];
    if (!found) {
      const isThinking = thinkingModels.includes(modelNameForSetting);
      const newSetting = {
        modelName: modelNameForSetting,
        temperature: 0,
        thinkingConfig: isThinking
          ? {
              enable: true,
              mode: "disabled",
              level: "minimal",
            }
          : null,
        selected: true,
      };
      setSelectedModelSetting(newSetting);
      newModelSettings.push(newSetting);
    }

    const isAuto = model.name === "auto";

    // ✅ Atomic update using "root" to ensure all fields are updated simultaneously
    onDataChange("root", {
      ...botData,
      botModel: isNoneOfThese ? null : modelNameForSetting,
      aiConfig: {
        ...botData.aiConfig,
        modelSettings: newModelSettings,
      },
      aiBotConfig: {
        ...botData.aiBotConfig,
        configId: null,
        model: null,
        ...(isAuto && {
          maestroFlowConfig: {
            ...(botData.aiBotConfig?.maestroFlowConfig || {}),
            selectedMode:
              botData.aiBotConfig?.maestroFlowConfig?.selectedMode ||
              "balanced",
          },
          selectedMode:
            botData.aiBotConfig?.maestroFlowConfig?.selectedMode || "balanced",
        }),
      },
    });
    handleDropdowns("model", false);
  };

  const handleConfigSelect = (configId: string) => {
    syncByokState(true);
    let model = configs.find((c) => c._id === configId)?.model;
    const configProvider = configs.find((c) => c._id === configId)?.provider;

    if (!model && byok && configProvider) {
      const p = configProvider.toLowerCase();
      const modelsArray = [AUTO_MODEL, ...models];
      const recommended = modelsArray.find((m) => {
        const n = (m.displayName || m.name || "").toLowerCase();
        if (p.includes("openrouter") || p.includes("open router"))
          return n.includes("openrouter");
        if (p.includes("google") || p.includes("gemini"))
          return n.includes("google") || n.includes("gemini");
        if (p.includes("openai") || p.includes("gpt"))
          return n.includes("openai") || n.includes("gpt");
        if (p.includes("anthropic") || p.includes("claude"))
          return n.includes("anthropic") || n.includes("claude");
        if (p.includes("deepseek")) return n.includes("deepseek");
        if (p.includes("groq"))
          return (
            n.includes("groq") || n.includes("kimi") || n.includes("llama")
          );
        return n.includes(p);
      });
      if (recommended) {
        model = recommended.name;
      }
    }

    const isAutoFamilyModel = isAutoFamilyModelName(model || "");
    const persistedModel = isAutoFamilyModel ? "auto" : model;
    const autoMode = isAutoFamilyModel
      ? getAutoModeFromModelName(model || "auto-balanced")
      : undefined;

    let newModelSettings = botData?.aiConfig?.modelSettings ?? [];
    let updatedSelectedModel = selectedModelSetting;

    if (persistedModel !== selectedModelSetting?.modelName) {
      let found = false;

      newModelSettings = newModelSettings.map((setting) => {
        if (setting.modelName === persistedModel) {
          found = true;
          updatedSelectedModel = setting;
          setSelectedModelSetting({
            ...setting,
            selected: true,
          });
          return {
            ...setting,
            selected: true,
          };
        }
        return {
          ...setting,
          selected: false,
        };
      });

      if (!found) {
        const isThinking = thinkingModels.includes(persistedModel ?? "");
        const newSetting = {
          modelName: persistedModel,
          temperature: 0,
          thinkingConfig: isThinking
            ? {
                enable: true,
                mode: "disabled",
                level: "minimal",
              }
            : null,
          selected: true,
        };
        setSelectedModelSetting(newSetting);
        newModelSettings.push(newSetting);
      }
    }

    // ✅ single onDataChange updating both aiConfig and aiBotConfig
    onDataChange("root", {
      ...botData,
      botModel: persistedModel,
      aiConfig: {
        ...botData?.aiConfig,
        modelSettings: newModelSettings,
      },
      aiBotConfig: {
        ...botData.aiBotConfig,
        configId,
        model: persistedModel,
        oldConfigSelected: configId,
        ...(isAutoFamilyModel && {
          maestroFlowConfig: {
            ...(botData.aiBotConfig?.maestroFlowConfig || {}),
            selectedMode: autoMode,
          },
          selectedMode: autoMode,
        }),
      },
    });

    handleDropdowns("configuration", false);
  };

  // AppSumo legacy: strip persisted "platform default" selection when no custom key is set,
  // or auto-select a key when BYOK is on — matches hiding Default Models in the selector UI.
  useEffect(() => {
    if (userDetails?.appSumo !== true || isNewUser) return;
    const rawConfigId = botData?.aiBotConfig?.configId;
    const configId =
      (typeof rawConfigId === "object" ? rawConfigId?._id : rawConfigId) ||
      null;
    if (configId) return;

    if (byok && configs.length > 0) {
      const targetId =
        defaultByokConfigId &&
        configs.some((c) => c._id === defaultByokConfigId)
          ? defaultByokConfigId
          : configs[0]._id;
      handleConfigSelect(targetId);
      return;
    }

    if (botData?.botModel != null) {
      onDataChange("root", {
        ...botData,
        botModel: null,
        aiConfig: getClearedAiConfig(botData),
        aiBotConfig: getClearedAiBotConfig(botData),
      });
      setSelectedModel(null);
      setSelectedModelSetting(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- migration runs when identity/plan/BYOK/config list change; handleConfigSelect is stable enough for this one-shot fix
  }, [
    userDetails?.appSumo,
    isNewUser,
    botData?.aiBotConfig?.configId,
    botData?.botModel,
    byok,
    configs.map((c) => c._id).join(),
    defaultByokConfigId,
  ]);

  const handleModelSourceChange = (
    source: "main" | "custom",
    provider?: string,
  ) => {
    if (source === "main") {
      if (userDetails?.appSumo === true && !isNewUser && configs.length === 0) {
        onDataChange("root", {
          ...botData,
          botModel: null,
          aiConfig: getClearedAiConfig(botData),
          aiBotConfig: getClearedAiBotConfig(botData),
        });
        setSelectedModel(null);
        setSelectedModelSetting(null);
        resetPreviewModelState(botData?.embedConfig);
        return;
      }
      syncByokState(false);
      if (plan?.name === "Lifetime") {
        if (botData.aiBotConfig?.configId) {
          onDataChange("aiBotConfig.configId", null);
        }
      } else {
        const shouldPreserveEmptySelection =
          hasExplicitlyClearedModelSelection(botData) &&
          !getPersistedModelName(botData);
        let savedDefaultName =
          botData?.botModel ?? (shouldPreserveEmptySelection ? null : "auto");
        const modelsArray = [AUTO_MODEL, ...models];

        const isModelAllowedInDefaultMode = (modelName?: string | null) => {
          if (!modelName) return true;
          const matchedModel = modelsArray.find((m) => m.name === modelName);
          if (!matchedModel) return true;

          return (
            plan?.name === "Trial" ||
            plan?.name === "outgrow" ||
            plan?.name === "Lifetime" ||
            (matchedModel.name === "auto" &&
              allowedModels?.some((am: any) => normalize(am) === "auto")) ||
            allowedModels?.some((am: any) => {
              const nm = normalize(matchedModel.name);
              const na = normalize(am);
              return (
                nm === na ||
                matchedModel.name.toLowerCase() === am.toLowerCase()
              );
            })
          );
        };

        const getRecommendedUnlockedModel = (
          providerName: string,
          fallbackModelName: string,
        ) => {
          const providerModels = modelsArray.filter(
            (m) => detectProvider(m) === providerName,
          );

          const recommendedUnlockedModel =
            providerModels.find(
              (m) =>
                m.displayName.toLowerCase().includes("(recommended)") &&
                isModelAllowedInDefaultMode(m.name),
            ) ||
            providerModels.find((m) => isModelAllowedInDefaultMode(m.name));

          return recommendedUnlockedModel?.name || fallbackModelName;
        };

        if (provider && provider !== "auto" && provider !== "all") {
          const pModels = models.filter((m) => {
            const n = (m.displayName || m.name || "").toLowerCase();
            const p = provider.toLowerCase();
            if (p === "google" || p === "gemini")
              return n.includes("google") || n.includes("gemini");
            if (p === "openai" || p.includes("gpt"))
              return n.includes("openai") || n.includes("gpt");
            if (p === "anthropic" || p.includes("claude"))
              return n.includes("anthropic") || n.includes("claude");
            if (p === "deepseek") return n.includes("deepseek");
            return n.includes(p);
          });

          // If the current platformModel doesn't match the requested provider,
          // OR if we don't have a platformModel, then pick the first one for this provider.
          const currentPlatformProvider = detectProvider({
            name: savedDefaultName,
          } as any);
          if (
            pModels.length > 0 &&
            (currentPlatformProvider !== provider ||
              !savedDefaultName ||
              savedDefaultName === "auto")
          ) {
            savedDefaultName = pModels[0].name;
          } else if (provider === "anthropic" && savedDefaultName === "auto") {
            savedDefaultName = "4.6-sonnet";
          }
        }

        if (!isModelAllowedInDefaultMode(savedDefaultName)) {
          const matchedModel = modelsArray.find(
            (m) => m.name === savedDefaultName,
          );
          const providerName = matchedModel
            ? detectProvider(matchedModel)
            : provider && provider !== "all"
              ? provider
              : null;

          if (providerName) {
            savedDefaultName = getRecommendedUnlockedModel(
              providerName,
              getRecommendedUnlockedModel("auto", "auto"),
            );
          } else {
            savedDefaultName = getRecommendedUnlockedModel("auto", "auto");
          }
        }

        const resolvedSelectedModel = savedDefaultName
          ? modelsArray.find((m) => m.name === savedDefaultName)
          : undefined;

        if (!savedDefaultName || !resolvedSelectedModel) {
          setSelectedModel(null);
          setSelectedModelSetting(null);
          resetPreviewModelState(botData?.embedConfig);

          onDataChange("root", {
            ...botData,
            botModel: null,
            aiConfig: getClearedAiConfig(botData),
            aiBotConfig: getClearedAiBotConfig(botData),
          });
          return;
        }

        setSelectedModel(resolvedSelectedModel);

        const updatedModelSettings =
          botData?.aiConfig?.modelSettings?.map((setting) => ({
            ...setting,
            selected: setting.modelName === savedDefaultName,
          })) || [];

        const hasResolvedSetting = updatedModelSettings.some(
          (setting) => setting.modelName === savedDefaultName,
        );
        if (!hasResolvedSetting) {
          updatedModelSettings.push({
            modelName: savedDefaultName,
            temperature: 0,
            thinkingConfig: null,
            selected: true,
          });
        }

        setSelectedModelSetting(
          updatedModelSettings.find((setting) => setting.selected) || null,
        );

        onDataChange("root", {
          ...botData,
          botModel: savedDefaultName,
          aiConfig: {
            ...botData.aiConfig,
            modelSettings: updatedModelSettings,
          },
          aiBotConfig: {
            ...botData.aiBotConfig,
            configId: null,
            model: null,
          },
        });
      }
    } else {
      syncByokState(true);
      if (!plan?.ai_currentLimits?.aiKey) return;
      if (!configs.length) {
        storeDispatch({
          type: "set-openConfigModalId",
          configId: "openai",
        });
        return;
      }
      if (!botData.aiBotConfig?.configId && configs.length) {
        const selectedConfigId =
          byok && defaultByokConfigId
            ? defaultByokConfigId
            : botData.aiBotConfig?.oldConfigSelected
              ? botData.aiBotConfig?.oldConfigSelected
              : botData.aiBotConfig?.configId
                ? botData.aiBotConfig?.configId
                : null;

        let model =
          byok && defaultByokConfigId
            ? configs.find((c) => c._id === defaultByokConfigId)?.model
            : botData.aiBotConfig?.oldConfigSelected
              ? configs.find(
                  (c) => c._id === botData.aiBotConfig?.oldConfigSelected,
                )?.model
              : botData.aiBotConfig?.configId
                ? configs.find((c) => c._id === botData.aiBotConfig?.configId)
                    ?.model
                : configs[0].model;

        if (!model && byok) {
          const cId = selectedConfigId;
          const configProvider = configs.find((c) => c._id === cId)?.provider;
          if (configProvider) {
            const p = configProvider.toLowerCase();
            const modelsArray = [AUTO_MODEL, ...models];
            const recommended = modelsArray.find((m) => {
              const n = (m.displayName || m.name || "").toLowerCase();
              if (p.includes("openrouter") || p.includes("open router"))
                return n.includes("openrouter");
              if (p.includes("google") || p.includes("gemini"))
                return n.includes("google") || n.includes("gemini");
              if (p.includes("openai") || p.includes("gpt"))
                return n.includes("openai") || n.includes("gpt");
              if (p.includes("anthropic") || p.includes("claude"))
                return n.includes("anthropic") || n.includes("claude");
              if (p.includes("deepseek")) return n.includes("deepseek");
              if (p.includes("groq"))
                return (
                  n.includes("groq") ||
                  n.includes("kimi") ||
                  n.includes("llama")
                );
              return n.includes(p);
            });
            if (recommended) {
              model = recommended.name;
            }
          }
        }

        let newModelSettings = botData?.aiConfig?.modelSettings ?? [];
        if (model !== selectedModelSetting?.modelName) {
          let found = false;

          newModelSettings = newModelSettings.map((setting) => {
            if (setting.modelName === model) {
              found = true;
              setSelectedModelSetting(setting);
              return { ...setting, selected: true };
            }
            return { ...setting, selected: false };
          });

          if (!found) {
            const isThinking = thinkingModels.includes(model ?? "");
            const newSetting = {
              modelName: model,
              temperature: 0,
              thinkingConfig: isThinking
                ? {
                    enable: true,
                    mode: "disabled",
                    level: "minimal",
                  }
                : null,
              selected: true,
            };
            setSelectedModelSetting(newSetting);
            newModelSettings.push(newSetting);
          }
        }

        // ✅ single onDataChange call
        onDataChange("root", {
          ...botData,
          aiConfig: {
            ...botData?.aiConfig,
            modelSettings: newModelSettings,
          },
          aiBotConfig: {
            ...botData?.aiBotConfig,
            configId: selectedConfigId,
          },
        });
      }
    }
  };

  useEffect(() => {
    if (botData?.aiBotConfig?.customPrompt) {
      const allVariables =
        botData?.aiBotConfig.customPrompt.matchAll(/{(\w*)}/g);
      setCustomPromptErr("");
      for (const variable of Array.from<string>(allVariables)) {
        if (!DEFAULT_CUSTOM_VARIABLE.includes(variable[1])) {
          setCustomPromptErr(`Not allowed: ${variable[0]}`);
          break;
        }
      }
    }
  }, [botData?.aiBotConfig?.customPrompt]);

  // Separate effect to set initial promptIndex when prompts are loaded
  useEffect(() => {
    if (hasInitializedPromptTab.current) return;
    if (!prompts || prompts.length === 0) return;

    const customPromptIndex = promptModification.findIndex(
      (prompt) => prompt.name === "Custom",
    );

    // Check if the custom prompt is exactly the same as the stored custom prompt
    if (
      botData?.aiBotConfig?.customPrompt &&
      botData?.aiBotConfig?.customPrompt ===
        botData?.aiBotConfig?.customPromptCustom
    ) {
      setPromptIndex(customPromptIndex);
      hasInitializedPromptTab.current = true;
      return;
    }

    // Find matching predefined prompt
    const matchedIndex = promptModification.findIndex((prompt) =>
      prompts?.some(
        (p) =>
          p.name === prompt.name &&
          p.prompt === botData?.aiBotConfig?.customPrompt,
      ),
    );

    setPromptIndex(matchedIndex !== -1 ? matchedIndex : customPromptIndex);
    hasInitializedPromptTab.current = true;
  }, [prompts, botData?.aiBotConfig?.customPrompt]);

  const clickOut = (event: Event) => {
    if (dropdownRef && !dropdownRef.current?.contains(event.target)) {
      handleDropdowns("model", false);
    }
    if (
      configDropdownRef &&
      !configDropdownRef.current?.contains(event.target)
    ) {
      handleDropdowns("configuration", false);
    }
    if (
      attributeDropdownRef &&
      !attributeDropdownRef.current?.contains(event.target)
    ) {
      handleDropdowns("attributes", false);
    }
    if (
      fullscreenAttributeDropdownRef &&
      !fullscreenAttributeDropdownRef.current?.contains(event.target)
    ) {
      handleDropdowns("fullscreenAttributes", false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", clickOut);
    return () => {
      document.removeEventListener("mousedown", clickOut);
    };
  }, []);

  useEffect(() => {
    if (!isPromptFullscreen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsPromptFullscreen(false);
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isPromptFullscreen]);

  useEffect(() => {
    if (!openAdvanceSettings) return;

    const timer = setTimeout(() => {
      advancedSettingsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);

    return () => clearTimeout(timer);
  }, [openAdvanceSettings]);

  function handleDropdowns(type, show = false) {
    if (isReadOnly) return;
    setDropdowns((prev) => ({
      ...prev,
      [type]: show,
    }));
  }

  function handlePromptChange(index) {
    if (isReadOnly) return;
    if (index === promptIndex) return;
    setPromptIndex(index);
    const promptName = promptModification[index].name;

    if (promptName === "Custom") {
      onDataChange("aiBotConfig", {
        ...botData.aiBotConfig,
        customPrompt: botData.aiBotConfig.customPromptCustom || "",
      });
      return;
    }

    // For other buttons, find the corresponding prompt
    const prompt = prompts.find((p) => p.name === promptName);

    if (prompt) {
      // Update the entire aiBotConfig object to ensure re-render
      onDataChange("aiBotConfig", {
        ...botData.aiBotConfig,
        customPrompt: prompt.prompt,
      });
    } else {
      console.warn(`No prompt found for: ${promptName}`);
    }
  }

  function handlePromptTextChange(customPrompt: string) {
    if (isReadOnly) return;

    const isCustomButton = promptModification[promptIndex]?.name === "Custom";
    const customPromptIndex = promptModification.length - 1;

    if (!isCustomButton) {
      setPromptIndex(customPromptIndex);
    }

    onDataChange("aiBotConfig", {
      ...botData.aiBotConfig,
      customPrompt,
      customPromptCustom: customPrompt,
    });
  }

  async function enhancePrompt() {
    if (isReadOnly) return;
    try {
      if (loader) return;
      if (!botData.aiBotConfig.customPrompt) return;
      setLoader(true);

      const response = await axiosPost("/ai/enhance-prompt", {
        prompt: botData.aiBotConfig.customPrompt,
      });

      handlePromptTextChange(response.data.data.prompt);
    } catch (err: any) {
      console.error(err);
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message:
            err.response?.data?.message ??
            err.message ??
            "Something went wrong, please try again.",
        }),
      );
    } finally {
      setLoader(false);
    }
  }

  function placeVariable(variable: any) {
    if (isReadOnly) return;
    const variableText = `{ ${variable.fieldId} }`;
    const activeEditor = isPromptFullscreen
      ? fullscreenEditorRef.current
      : editorRef.current;

    if (activeEditor) {
      activeEditor.insertAtCursor(variableText);
    }

    // Read back the updated markdown from the editor
    const customPrompt = activeEditor?.getValue() || "";

    onDataChange("aiBotConfig", {
      ...botData.aiBotConfig,
      customPrompt: customPrompt,
      customPromptCustom: customPrompt,
    });

    setPrompts((prevPrompts) => {
      const isCustomExists = prevPrompts?.some((p) => p.name === "Custom");
      return isCustomExists
        ? prevPrompts.map((p) =>
            p.name === "Custom" ? { ...p, prompt: customPrompt } : p,
          )
        : [...prevPrompts, { name: "Custom", prompt: customPrompt }];
    });

    setPromptIndex(promptModification.length - 1);

    setTimeout(() => {
      activeEditor?.focus();
    }, 0);
  }

  const initialBots = useMemo(
    () => [
      {
        _id: botData?._id || botData?.botCustomId || "",
        botCustomId: botData?.botCustomId || "",
        projectName:
          botData?.projectName ||
          botData?.name ||
          botData?.title ||
          "Untitled Agent",
        botModel: botData?.botModel,
        aiConfig: botData?.aiConfig,
        aiBotConfig: botData?.aiBotConfig,
        maestroFlowConfig: botData?.maestroFlowConfig,
        aiSource: botData?.aiSource,
      },
    ],
    [botData],
  );

  return (
    <>
      {fromOutgrow && (
        <style>{`
          .status_dropdown_list li:hover,
          .dropdown-container li:hover {
            background: #eaf4fb !important;
            color: #444 !important;
          }
          .status_dropdown_list li.active-item,
          .dropdown-container li.active-item {
            background: #eaf4fb !important;
            color: #444 !important;
          }
          // .status_dropdown_list li:hover svg path,
          // .dropdown-container li:hover svg path,
          // .status_dropdown_list li.active-item svg path,
          // .dropdown-container li.active-item svg path {
          //   fill: #ffffff !important;
          //   stroke: #ffffff !important;
          // }
        `}</style>
      )}
      <div
        className={`ai-info-container flex h-max max-h-full w-full !overflow-hidden ${
          hideApiKeyOption ? "onboarding-ai-settings" : ""
        }`}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="flex h-full w-full flex-col gap-[20px] overflow-auto"
        >
          {/* AI Model Selection */}
          <UnifiedModelSelector
            selectedModel={selectedModel}
            isCustomModel={isCustomModel}
            configs={configs}
            botData={botData}
            byok={byok}
            defaultByokConfigId={defaultByokConfigId}
            allowedModels={allowedModels}
            plan={plan}
            handleModelSelect={handleModelSelect}
            onTryNow={handleTryNow}
            hideApiKeyOption={shouldHideByokOptions}
            hideModelQualities={hideModelQualities}
            readOnly={isReadOnly}
            canManageProviderKeys={canManageProviderKeys}
            handleConfigSelect={handleConfigSelect}
            handleModelSourceChange={handleModelSourceChange}
            onAddNewConfig={() => {
              if (!canManageProviderKeys) return;
              if (shouldHideByokOptions) return;
              setSourceConfigForNewModel(undefined);
              setEditingConfigId(undefined);
              setEditingConfigProvider(undefined);
              setByokOnboardingMode(true);
            }}
            onEditConfig={(config) => {
              if (!canManageProviderKeys) return;
              updateState(config);
            }}
            onAddModelToConfig={(config) => {
              if (!canManageProviderKeys) return;
              setSourceConfigForNewModel(config);
              setEditingConfigId(undefined);
              setEditingConfigProvider(config.provider);
              setOnboardingFlowMode("add");
              setByokOnboardingMode(true);
            }}
            onDirectModelUpdate={handleDirectModelUpdate}
            onUpdateConfig={
              canManageProviderKeys ? handleUpdateConfig : undefined
            }
            onDeleteConfig={(configId) => {
              if (!canManageProviderKeys) return;
              setShowDeleteModal(true);
              setConfigIdToDelete(configId);
            }}
            onMaestroModeChange={(mode) => {
              syncByokState(false);
              // ✅ Atomic update: always include botModel: "auto" when setting maestro mode
              // Without this, the update uses stale botData and overwrites botModel back to the old model
              setSelectedModel(AUTO_MODEL);
              const modelNameForSetting = "auto";

              // Update modelSettings to select "auto"
              const newModelSettings = (
                botData?.aiConfig?.modelSettings || []
              ).map((s: any) =>
                s.modelName === "auto"
                  ? { ...s, selected: true }
                  : { ...s, selected: false },
              );

              // If "auto" was not in settings, add it (though it usually is)
              if (!newModelSettings.some((s: any) => s.modelName === "auto")) {
                newModelSettings.push({
                  modelName: "auto",
                  temperature: 0,
                  thinkingConfig: null,
                  selected: true,
                });
              }

              onDataChange("root", {
                ...botData,
                botModel: "auto",
                aiConfig: {
                  ...botData.aiConfig,
                  modelSettings: newModelSettings,
                },
                aiBotConfig: {
                  ...botData.aiBotConfig,
                  model: "auto",
                  oldConfigSelected: null,
                  configId: botData?.aiBotConfig?.configId || null,
                  maestroFlowConfig: {
                    ...(botData.aiBotConfig?.maestroFlowConfig || {}),
                    selectedMode: mode,
                  },
                  selectedMode: mode, // top level inside aiBotConfig for convenience
                },
                // Keep root level selectedMode and maestroFlowConfig if needed for other parts of UI
                selectedMode: mode,
                maestroFlowConfig: {
                  ...(botData.maestroFlowConfig || {}),
                  selectedMode: mode,
                },
              });
            }}
          />

          {/* AI Prompt Section */}
          <div className="flex h-max max-h-full flex-col gap-[10px]">
            <div className="flex flex-wrap justify-between gap-[12px]">
              <div className="al-cnt flex gap-8">
                <h3 className="w-max text-[14px] font-medium">AI Prompt</h3>
                <button
                  className={`pr-line-btn flex w-max items-center gap-6 ${loader ? "loading_stars" : ""} ${hideApiKeyOption ? "h-[24px] rounded-[8px] !px-[6px] !py-[8px]" : ""}`}
                  onClick={enhancePrompt}
                >
                  {loader ? (
                    <>
                      <StarsIcon2 />
                      <div className="flex items-baseline">
                        Enhancing
                        <ThreeDot />
                      </div>
                    </>
                  ) : (
                    <div
                      className={`flex items-center ${hideApiKeyOption ? "text-[11px]" : ""}`}
                    >
                      <PromptIcon
                        className={hideApiKeyOption ? "h-[12px] w-[12px]" : ""}
                      />{" "}
                      Enhance {!hideApiKeyOption ? "Prompt" : ""}
                    </div>
                  )}
                </button>
              </div>
              {!hideApiKeyOption && (
                <div className="flex items-center gap-8">
                  <div
                    className="p-relative ai-attribute-dropdown ai-attr-dropdown"
                    ref={attributeDropdownRef}
                  >
                    <div
                      className="email-generator-dropdown botinfo_inputs fx jstfy-sp-btw al-cnt g_dropdownHover !h-[unset] bg-white !py-[10px]"
                      onClick={() =>
                        handleDropdowns("attributes", !dropdowns.attributes)
                      }
                    >
                      <div className="dropdown-text fx al-cnt mb-0 gap-10 !text-[12px]">
                        Select Attribute
                      </div>
                      <div>
                        <svg
                          width="10"
                          height="5"
                          viewBox="0 0 10 5"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0 -0.000488281L5 4.99951L10 -0.000488281H0Z"
                            fill="#6D6D6D"
                          />
                        </svg>
                      </div>
                    </div>
                    <AnimatePresence>
                      {dropdowns.attributes && (
                        <motion.ul
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                          className="dropdown-container grey-scrollbar !top-[40px] !w-[98%]"
                        >
                          {[
                            ...attributeData?.attributes,
                            {
                              label: "Detected Location",
                              fieldId: "detected_location",
                              type: "text",
                            },
                          ].map((option, idx) => (
                            <li
                              key={idx}
                              className="fx al-cnt gap-10"
                              onClick={() => {
                                placeVariable(option);
                                handleDropdowns("attributes", false);
                              }}
                            >
                              {option.label}
                            </li>
                          ))}
                          <li
                            className="fx al-cnt gap-10"
                            onClick={() => {
                              placeVariable({ fieldId: "parent_url" });
                              handleDropdowns("attributes", false);
                            }}
                          >
                            Website Url
                          </li>
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </div>
            <div
              className="aisetting_tarea_wrapper tab5_tarea relative overflow-hidden rounded-[10px] border bg-[#fafafa] text-[14px]"
              style={{ height: `${editorHeight}px`, minHeight: "250px" }}
            >
              <div className="relative h-full">
                <TiptapEditor
                  ref={editorRef}
                  value={botData?.aiBotConfig?.customPrompt || ""}
                  previewMode="preview"
                  maxLength={customPromptMaxLength}
                  onChange={handlePromptTextChange}
                  placeholder="Provide custom prompt"
                  height={editorHeight}
                  hideToolbar={true}
                  visibleDragBar={true}
                  readOnly={isReadOnly}
                />
              </div>
              <button
                className="absolute right-[10px] top-[5px] z-[2] flex h-[24px] w-[24px] items-center justify-center gap-4 rounded-md border border-black/10 bg-white px-2 py-1 text-[12px] text-gray-600 transition-colors hover:bg-gray-100"
                onClick={() => setIsPromptFullscreen(true)}
                title="Fullscreen editor"
              >
                <svg
                  className="flex-shrink-0"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              </button>
              <div
                className="absolute bottom-0 left-0 right-0 h-[8px] cursor-ns-resize bg-transparent transition-colors hover:bg-[#eaf4fb]"
                onMouseDown={(e) => {
                  e.preventDefault();
                  const startY = e.clientY;
                  const startHeight = editorHeight;

                  const handleMouseMove = (moveEvent: MouseEvent) => {
                    const deltaY = moveEvent.clientY - startY;
                    const newHeight = Math.max(250, startHeight + deltaY);
                    setEditorHeight(newHeight);
                  };

                  const handleMouseUp = () => {
                    document.removeEventListener("mousemove", handleMouseMove);
                    document.removeEventListener("mouseup", handleMouseUp);
                  };

                  document.addEventListener("mousemove", handleMouseMove);
                  document.addEventListener("mouseup", handleMouseUp);
                }}
              >
                <div className="absolute bottom-[2px] left-1/2 h-[3px] w-[40px] -translate-x-1/2 transform rounded-full bg-gray-400"></div>
              </div>
            </div>
            {customPromptErr && (
              <p className="mt-2 text-sm text-red-500">{customPromptErr}</p>
            )}
          </div>

          {/* Fullscreen Prompt Editor Modal */}
          <AnimatePresence>
            {isPromptFullscreen && (
              <ModalWrapper>
                <motion.div
                  className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  onClick={(e) => {
                    if (e.target === e.currentTarget)
                      setIsPromptFullscreen(false);
                  }}
                >
                  <motion.div
                    className="relative flex h-max max-h-[90vh] w-[90vw] max-w-[1180px] flex-col rounded-xl bg-white shadow-2xl"
                    initial={{ opacity: 0, scale: 0.92, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.92, y: 30 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  >
                    <button
                      className="absolute right-[-20px] top-[-20px] text-white"
                      onClick={() => setIsPromptFullscreen(false)}
                      title="Exit fullscreen (Esc)"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                    {/* Modal Header */}
                    <div className="flex items-center justify-between border-b px-6 py-4">
                      <div className="al-cnt flex gap-8">
                        <h3 className="text-[16px] font-semibold">AI Prompt</h3>
                        <button
                          className={`pr-line-btn flex items-center gap-6 ${loader ? "loading_stars" : ""}`}
                          onClick={enhancePrompt}
                        >
                          {loader ? (
                            <>
                              <StarsIcon2 />
                              <div className="flex items-baseline">
                                Enhancing
                                <ThreeDot />
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center">
                              <PromptIcon
                                className={
                                  hideApiKeyOption ? "h-[12px] w-[12px]" : ""
                                }
                              />{" "}
                              Enhance Prompt
                            </div>
                          )}
                        </button>
                      </div>
                      <div className="flex items-center gap-4">
                        <div
                          className="p-relative ai-attribute-dropdown ai-attr-dropdown"
                          ref={fullscreenAttributeDropdownRef}
                        >
                          <div
                            className="email-generator-dropdown fx jstfy-sp-btw al-cnt g_dropdownHover !h-[36px] bg-white !py-[10px]"
                            onClick={() =>
                              handleDropdowns(
                                "fullscreenAttributes",
                                !dropdowns.fullscreenAttributes,
                              )
                            }
                          >
                            <div className="dropdown-text fx al-cnt mb-0 gap-10 !text-[12px]">
                              Select Attribute
                            </div>
                            <div>
                              <svg
                                width="10"
                                height="5"
                                viewBox="0 0 10 5"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M0 -0.000488281L5 4.99951L10 -0.000488281H0Z"
                                  fill="#6D6D6D"
                                />
                              </svg>
                            </div>
                          </div>
                          <AnimatePresence>
                            {dropdowns.fullscreenAttributes && (
                              <motion.ul
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                                className="dropdown-container grey-scrollbar"
                              >
                                {[
                                  ...attributeData?.attributes,
                                  {
                                    label: "Detected Location",
                                    fieldId: "detected_location",
                                    type: "text",
                                  },
                                ].map((option, idx) => (
                                  <motion.li
                                    key={idx}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{
                                      duration: 0.15,
                                      delay: idx * 0.05,
                                    }}
                                    className="fx al-cnt gap-10"
                                    onClick={() => {
                                      placeVariable(option);
                                      handleDropdowns(
                                        "fullscreenAttributes",
                                        false,
                                      );
                                    }}
                                  >
                                    {option.label}
                                  </motion.li>
                                ))}
                                <motion.li
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{
                                    duration: 0.15,
                                    delay:
                                      (attributeData?.attributes.length || 0) *
                                      0.05,
                                  }}
                                  className="fx al-cnt gap-10"
                                  onClick={() => {
                                    placeVariable({ fieldId: "parent_url" });
                                    handleDropdowns(
                                      "fullscreenAttributes",
                                      false,
                                    );
                                  }}
                                >
                                  Website Url
                                </motion.li>
                              </motion.ul>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </div>
                    {/* Modal Editor Body */}
                    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden rounded-b-xl bg-[#fafafa] p-4">
                      <div className="relative min-h-0 flex-1">
                        <TiptapEditor
                          ref={fullscreenEditorRef}
                          value={botData?.aiBotConfig?.customPrompt || ""}
                          previewMode="preview"
                          style={{ height: "100%" }}
                          onChange={handlePromptTextChange}
                          placeholder="Provide custom prompt"
                          hideToolbar={true}
                          visibleDragBar={false}
                          readOnly={isReadOnly}
                        />
                      </div>
                      <div className="flex justify-center">
                        <button
                          className="rounded-lg bg-[#FB5F66] px-6 py-2 font-medium text-white transition-colors hover:shadow-[0px_4px_20px_rgba(251,95,102,0.15)]"
                          onClick={() => setIsPromptFullscreen(false)}
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </ModalWrapper>
            )}
          </AnimatePresence>

          {/* Prompt Modification Buttons */}
          <div className="flex flex-col gap-[10px]">
            <p
              className={`capitalize ${hideApiKeyOption ? "text-[14px] font-medium text-[#444]" : "text-[13px] text-[#828282]"}`}
            >
              Modify the prompt
            </p>
            <div className="prompt-field flex flex-wrap gap-[10px]">
              {promptModification.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => handlePromptChange(index)}
                  className={`${
                    index === promptIndex
                      ? fromOutgrow
                        ? "active-outgrow"
                        : "active"
                      : ""
                  } prompt-btn flex gap-[6px] rounded-[8px] border border-light-shade bg-light-shade p-[10px] text-left text-text-clr transition-all duration-500 ease-in-out hover:border-[1px] ${
                    fromOutgrow
                      ? "hover:border-[#3e9adc] hover:bg-[rgba(62,154,220,0.05)] hover:text-[#3e9adc]"
                      : "hover:border-primary-clr hover:bg-[rgba(255,120,126,0.05)] hover:text-primary-clr"
                  } ${hideApiKeyOption ? "text-[12px]" : ""}`}
                >
                  {prompt.image}
                  {prompt.name}
                </button>
              ))}
            </div>
          </div>
          {(() => {
            const advancedConfig = aiConfigElements.find(
              (el) =>
                normalize(el.modelName) ===
                normalize(selectedModel?.name ?? ""),
            );
            const canShowAdvancedSettings =
              selectedModel?.name !== "auto" &&
              !!advancedConfig &&
              (advancedConfig.tempControl || advancedConfig.thinkingToggle);

            if (
              !canShowAdvancedSettings ||
              hideApiKeyOption ||
              hideAdvancedSettings
            )
              return null;

            return (
              <div
                className="flex flex-col gap-[20px]"
                ref={advancedSettingsRef}
              >
                <p
                  className="flex cursor-pointer items-center gap-[16px] text-[14px] leading-[15.4px] text-[#fb5f66]"
                  onClick={() => {
                    setOpenAdvanceSettings(!openAdvanceSettings);
                  }}
                >
                  Advance Settings
                  <svg
                    className={`transition-transform ${openAdvanceSettings ? "rotate-180" : ""}`}
                    xmlns="http://www.w3.org/2000/svg"
                    width={11}
                    height={7}
                    viewBox="0 0 11 7"
                    fill="none"
                  >
                    <path
                      d="M0 0.791626L5.20833 6.20829L10.4167 0.791626L0 0.791626Z"
                      fill="#FB5F66"
                    />
                  </svg>
                </p>
                <AnimatePresence>
                  {openAdvanceSettings && (
                    <AIConfiguration
                      selectedModelSetting={selectedModelSetting}
                      setSelectedModelSetting={setSelectedModelSetting}
                      onDataChange={onDataChange}
                      botData={botData}
                      selectedModel={selectedModel}
                    />
                  )}
                </AnimatePresence>
              </div>
            );
          })()}
        </motion.div>

        {/* Modal for OpenAI Configuration */}
        <ModalWrapper
          open={canManageProviderKeys && byokOnboardingMode}
          setOpen={setByokOnboardingMode}
          width="600px"
        >
          <ByokOnboarding
            userId={userDetails?.user?._id || ""}
            initialBots={initialBots}
            mode={onboardingFlowMode}
            onSuccess={() => {
              setByokOnboardingMode(false);
              setOnboardingFlowMode("onboarding");
              setSourceConfigForNewModel(undefined);
              getAllAIConfig();
              // Trigger internal re-render by refetching bot data and user details
              const botUrl = botData?.url;
              if (botUrl) {
                queryClient.invalidateQueries({
                  queryKey: ["bot", "completeBot", { botUrl }],
                });
              }
              queryClient.invalidateQueries({
                queryKey: ["v1", "bot", "attributes", botData.botCustomId],
              });
              queryClient.invalidateQueries({ queryKey: ["userDetails"] });
            }}
            inline={true}
            onClose={() => {
              setByokOnboardingMode(false);
              setOnboardingFlowMode("onboarding");
              setSourceConfigForNewModel(undefined);
              getAllAIConfig();
              const botUrl = botData?.url;
              if (botUrl) {
                queryClient.invalidateQueries({
                  queryKey: ["bot", "completeBot", { botUrl }],
                });
              }
              queryClient.invalidateQueries({ queryKey: ["userDetails"] });
            }}
            configId={editingConfigId || ""}
            provider={editingConfigProvider}
            sourceConfig={sourceConfigForNewModel}
          />
        </ModalWrapper>

        {canManageProviderKeys && showDeleteModal && (
          <ModalWrapper>
            <DeleteAgentList
              heading="Are you sure you want to delete this API key"
              subHeading="This action cannot be undone"
              onClose={() => setShowDeleteModal(false)}
              onDelete={() => {
                handleDelete(configIdToDelete);
              }}
            />
          </ModalWrapper>
        )}

        {leaveModal && (
          <ModalWrapper>
            <BooleanModal
              onConfirm={() => {
                if (fromOutgrow) {
                  sendEventToOutgrow({
                    type: "navigate",
                    payload: {
                      route: "/settings?tab=8",
                    },
                  });
                } else {
                  dispatch(userSlice.actions.setSettings(8));
                  navigate("/settings?tab=8");
                }
              }}
              onCancel={() => setLeaveModal(false)}
              resetCharacterCountOnConfirm={false}
            />
          </ModalWrapper>
        )}
      </div>
    </>
  );
}

export default AIBotTab_5;
